import React, { useEffect, useMemo, useState } from 'react';
import './entity-popup.scss';
import { Input } from 'antd';
import CustomIcon, { IconType } from '../../custom-icon/custom-icon';
import { ListItem } from '../entity-selector';
import { EntityPopupItem } from './entity-popup-item';
import { EditorMode } from '../../../../utils/enums';
import { Column, CrudConfig } from '../../../../model/table';
import { toCamelCase } from '../../../../utils/string-normalization';
import { DataEditorModal } from '../../edit-row-modal/edit-row-modal';
import SearchIcon from '@svg/search.svg?react';

const SelectAllLabel: React.FC<{ data: any }> = ({ data }) => <div>{data?.label}</div>;

interface EntityPopupProps<T> {
    entityName: { singular: string; plural: string };
    fullList: ListItem[];
    selectedItems: string[];
    handleSelection: (key: string, checked: boolean) => void;
    handleBulkAllSelection?: (keys: string[], checked: boolean) => void;
    EntityLabel: React.FC<{ data: any }>;
    EntityInfo?: React.FC<{ data: any }>;
    crudConfig?: CrudConfig<T>;
    columnsConfig?: Column<T>[];
    isNewEntityCreatable: boolean;
}

export const EntityPopup = <T,>(props: EntityPopupProps<T>) => {
    const { entityName, fullList, selectedItems, handleSelection, handleBulkAllSelection, EntityLabel, EntityInfo, crudConfig, columnsConfig, isNewEntityCreatable  } = props;
    const [searchValue, setSearchValue] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAllSelected, setIsAllSelected] = useState(false);

    useEffect(() => {
        setIsAllSelected(selectedItems.length === fullList.length);
    }, [selectedItems]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
    };

    const handleSwitchChange = (checked: boolean, item: ListItem) => {
        handleSelection(item.key, checked);
    };

    const handleSwitchChangeToAll = (checked: boolean) => {
        handleBulkAllSelection?.(fullList.map(_ => _.key), checked);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handleNewEntityClick = () => {
        setIsModalOpen(true);
    };

    const save = async (editedRowData: T) => {
        await crudConfig?.saveCb?.(editedRowData);
    };

    const Renderer = EntityPopupItem;

    const items = useMemo(() => {
        return fullList.filter(item => item.name.toLowerCase().includes(searchValue.toLowerCase())).map(item => {
            const isSelected = selectedItems.includes(item.key);
            return (
                <div className={'entity-item-wrapper'} key={item.key}>
                    {<Renderer EntityLabel={EntityLabel}
                        EntityInfo={EntityInfo}
                        isSelected={isSelected}
                        item={item}
                        onChange={(checked: boolean, entity: ListItem) => handleSwitchChange(checked, entity)} />}
                </div>
            );
        });
    }, [fullList, selectedItems, searchValue]);

    return (
        <>
            <div className={'entity-list-header'}>
                <span className={'title'}>{selectedItems.length ? `${selectedItems.length} selected` : `Select ${entityName.plural}`}</span>
            </div>
            <Input
                className={'search-input'}
                placeholder={`Search`}
                value={searchValue}
                onChange={handleSearchChange}
                prefix={<SearchIcon />}
                allowClear
            />
            {!!items.length && !searchValue && <div>
                <Renderer EntityLabel={SelectAllLabel}
                    isSelected={isAllSelected} onChange={(checked) => handleSwitchChangeToAll(checked)}
                    item={{
                        key: 'selected-all-key',
                        name: '',
                        data: { label: 'Select All' }
                    }} />
            </div>}
            <div className={'entity-list'}>{items}</div>
            {crudConfig && columnsConfig && isNewEntityCreatable && <div className="add-entity-wrapper">
                <button type="button" className={'button-link add-entity-button'}
                    onClick={handleNewEntityClick}>
                    <CustomIcon type={IconType.PLUS} size={10} />
                    {`New ${entityName.singular}`}
                </button>
                <DataEditorModal
                    isOpen={isModalOpen}
                    closeModal={closeModal}
                    rowData={crudConfig?.newRecord as T}
                    columnsConfig={columnsConfig}
                    onSave={save}
                    crudConfig={crudConfig}
                    mode={EditorMode.NEW}
                    header={toCamelCase(`Add ${entityName.singular}`)}
                    okBtnText={'Add'}
                    duplicationValidator={crudConfig?.isDuplicated}
                    data={fullList.map(_ => _.data)}
                />
            </div>}
        </>
    );
};

export default EntityPopup;